import React, { useState } from "react";
import "./HealthCareSign.scss";
import "./SuperAdminSignIn.scss";
import logo from "../../../assets/images/MainLogo.png";
import TextInput from "../../../components/TextInput/TextInput";
import Buttons from "../../../components/Buttons/Buttons";
import { useNavigate } from "react-router-dom";
import { UserLogin } from "../../../utils/apiCalls/auth";
import { API_CONSTANTS } from "../../../utils/api-constant";
import Spinner from "../../../components/Spinner/Spinner";
import { setPendingUserId } from "../../../services/auth";
import { useToast } from "../../../utils/ToastContext";

const MicrosoftLogo = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const initialValues = {
  userName: "",
  password: "",
};

// loginType: 1 = Super Admin, 2 = Health Professional. Renders identically
// to the two pages this replaced (HealthCareSign/SuperAdminSignIn), keeping
// each portal's original class prefix/stylesheet so the visuals don't shift.
const SignIn = ({ loginType }) => {
  const isSuperAdmin = loginType === 1;
  const classPrefix = isSuperAdmin ? "signin" : "hcsignin";
  const navigate = useNavigate();
  const [login, setLogin] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLogin((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  const validateFields = () => {
    let errObj = { ...initialValues };

    if (!login.userName) {
      errObj.userName = "This field is required";
    } else {
      errObj.userName = "";
    }

    if (!login.password) {
      errObj.password = "This field is required";
    } else if (login.password.length < 8) {
      errObj.password = "Password must be at least 8 characters";
    } else {
      errObj.password = "";
    }

    setErrors(errObj);
    return Object.values(errObj).every((x) => x === "" || x === null);
  };

  const handleSignIn = async () => {
    if (isLoading) return;
    setIsLoading(true);
    if (validateFields()) {
      const payload = {
        userName: login.userName,
        password: login.password,
        loginType,
      };
      try {
        const res = await UserLogin(payload);
        const { statusCode, data, message } = res;
        if (statusCode === 200) {
          // No session token is stored here: the account isn't
          // authenticated until OTP verification succeeds below.
          setPendingUserId(data.userId);
          setIsLoading(false);
          navigate("/otp-verification");
        } else {
          setIsLoading(false);
          showToast(message, "error");
        }
      } catch (error) {
        setIsLoading(false);
        showToast(error?.message, "error");
      }
    } else {
      setIsLoading(false);
    }
  };

  // Must be a browser navigation, not XHR: the BE sets the entra_binding
  // cookie and 302s to Entra, which comes back via /auth/callback (AuthCallback).
  const handleMicrosoftSignIn = () => {
    window.location.assign(API_CONSTANTS.ENTRA_LOGIN);
  };

  return (
    <div className={classPrefix}>
      {isLoading && (
        <div className="loader-container">
          <div className="loader">
            <Spinner />
          </div>
        </div>
      )}
      <div className={`${classPrefix}_bcg`}>
        <div className={`${classPrefix}_container`}>
          <div className={`${classPrefix}_section`}>
            <div className={`${classPrefix}_section_logo`}>
              <img src={logo} alt="logo" />
            </div>
            <div className={`${classPrefix}_section_header`}>
              <h3>{isSuperAdmin ? "WeBe Admin Portal" : "WeBe Data Portal"}</h3>
              <p>
                {isSuperAdmin
                  ? "Enter your details below to continue"
                  : "Sign in with your organisation account or email"}
              </p>
            </div>
            {/* SSO is HP-only; Super Admin stays on password + OTP. */}
            {!isSuperAdmin && (
              <>
                <div className={`${classPrefix}_section_sso`}>
                  <Buttons
                    text={"Sign in with Microsoft"}
                    variant="secondary"
                    icon={<MicrosoftLogo />}
                    onClick={handleMicrosoftSignIn}
                    disabled={isLoading}
                  />
                </div>
                <div className={`${classPrefix}_section_divider`}>
                  <span>or sign in with email</span>
                </div>
              </>
            )}
            <div className={`${classPrefix}_section_inputs`}>
              <div className={`${classPrefix}_section_inputs_data`}>
                <TextInput
                  title={"Enter User Name/Email"}
                  type={"text"}
                  placeholder={"Enter Your User Name/Email"}
                  name="userName"
                  value={login.userName}
                  icon="mail"
                  onChange={handleChange}
                  error={!!errors.userName}
                  helperText={errors.userName}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSignIn();
                    }
                  }}
                />
              </div>
              <div className={`${classPrefix}_section_inputs_data`}>
                <TextInput
                  title={"Password"}
                  type={"password"}
                  placeholder={"********"}
                  name="password"
                  icon="lock"
                  value={login.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSignIn();
                    }
                  }}
                />
              </div>
            </div>
            {!isSuperAdmin && (
              <div className={`${classPrefix}_section_forgotpws`}>
                <button type="button" className="forgotpws-link" onClick={handleForgotPassword}>
                  Forgot Password
                </button>
              </div>
            )}
            <div className={`${classPrefix}_section_buttons`}>
              <Buttons
                text={"Sign In"}
                variant="primary"
                onClick={handleSignIn}
                disabled={isLoading}
              />
            </div>
            {!isSuperAdmin && (
              <div className={`${classPrefix}_section_register`}>
                <span>Don&apos;t have an account?</span>
                <button
                  type="button"
                  className="register-link"
                  onClick={() => navigate("/register")}
                >
                  Register
                </button>
              </div>
            )}
          </div>
        </div>
        <div className={`${classPrefix}_bcglayout`}></div>
      </div>
    </div>
  );
};

export default SignIn;
